import { isMeetingSessionRoleGreaterThan, isRoleGreaterThanOrEqual, MeetingContainerAPI, MeetingSessionOvenMediaReceiverManagerBaseData, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { Meeting, MeetingRoom, MeetingRoomAudienceType, MeetingRoomHighlightingState, MeetingRoomStatus, MeetingRoomType, MeetingSession, MeetingSessionHighlightingState, MeetingSessionRole, MeetingSessionStatus, UserRole } from "@kalyzee/kast-web-sdk/websocket";
import { useMemo } from "react";
import { Activity, PopulatedMeeting, PopulatedRoom, PopulatedRooms, PopulatedSession, PopulatedUser } from "./interfaces";

const _createPopulatedUsers = (
  meeting: Meeting,
  sessions: MeetingSession[],
  ownSession: MeetingSession,
  receptionSessions: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>[],
): {  
  me: PopulatedUser;
  users: PopulatedUser[];
  studentUsers: PopulatedUser[];
  expertUsers: PopulatedUser[];
} => {
  const userMap = new Map<string, PopulatedUser>();
  const sessionMap = new Map<string, PopulatedSession>();

  meeting.actors?.forEach((actor) => {
    const { user } = actor;
    if (!user) return;
    const populatedUser: PopulatedUser = {
      id: user.id,
      user,
      actor,
      sessions: [],
      active: false,
    };
    userMap.set(populatedUser.id, populatedUser);
  });

  sessions.forEach((session) => {
    const user = session.user;
    if (!user) return;
    let populatedUser = userMap.get(user.id);
    if (!populatedUser) {
      // User without actor
      populatedUser = {
        id: user.id,
        user,
        sessions: [],
        active: false,
      };
      userMap.set(populatedUser.id, populatedUser);
    }
    if (session.status === MeetingSessionStatus.RUNNING) {
      populatedUser.active = true;
    }
    const populatedSession: PopulatedSession = {
      id: session.id,
      session,
      videoSessions: [],
      user: populatedUser,
      increment: 0,
    };
    sessionMap.set(populatedSession.id, populatedSession);
    populatedUser.sessions.push(populatedSession);
  });

  meeting.usersData?.forEach((userData) => {
    const populatedUser = userMap.get(userData.userId);
    if (!populatedUser) return;
    populatedUser.data = userData;
  });

  (receptionSessions ?? []).forEach((session) => {
    const sessionId = session.data.session.id;
    const aggregatedSession = sessionMap.get(sessionId);
    if (!aggregatedSession) return;
    aggregatedSession.videoSessions.push(session);
  });

  const studentUsers: PopulatedUser[] = [];
  const expertUsers: PopulatedUser[] = [];

  userMap.forEach((user) => {
    if (isRoleGreaterThanOrEqual(user.user.role, UserRole.ORGANIZER)) {
      expertUsers.push(user);
    } else {
      studentUsers.push(user);
    }
  });

  const ownUser = ownSession.user!;
  let me = userMap.get(ownUser.id);
  if (!me) {
    // My user was not found in actors and sessions
    me = {
      id: ownUser.id,
      user: ownUser,
      sessions: [],
      active: false,
    };
  }

  return {
    me,
    users: Array.from(userMap.values()),
    studentUsers,
    expertUsers,
  };
};

const _createAndInjectPopulatedRooms = (rooms: MeetingRoom[], users: PopulatedUser[]): PopulatedRooms => {
  const groups: PopulatedRoom[] = [];
  const waitingRooms: PopulatedRoom[] = [];
  let activityRoom: PopulatedRoom | undefined;

  rooms.forEach((room) => {
    const pUsers = users.filter((user) => (room.enlistedUserIds ?? []).includes(user.id));

    const pRoom: PopulatedRoom = {
      id: room.id,
      room,
      users: pUsers,
    };

    if (room.type === MeetingRoomType.Group) {
      pUsers.forEach((user) => (user.group = pRoom));
      groups.push(pRoom);
    } else if (room.type === MeetingRoomType.WaitingRoom) {
      waitingRooms.push(pRoom);
    } else {
      activityRoom = pRoom;
    }
  });

  return { groups, waitingRooms, activityRoom };
};

const _createActivityData = (rooms: PopulatedRooms, users: PopulatedUser[]): Activity | undefined => {
  const { groups, activityRoom } = rooms;

  if (!activityRoom) return undefined;
  const { audience, enlistedUserIds } = activityRoom.room;

  if (activityRoom.room.status === MeetingRoomStatus.Closed) return undefined;

  let outputData: Activity | undefined = undefined;

  // 1 - we collect the users who respect the constraint

  if (audience?.type === MeetingRoomAudienceType.RoomMember) {
    if (audience.values === "*") return undefined; // not supported for now
    const visibleGroupId = audience.values.length ? audience.values[0] : undefined;
    if (!visibleGroupId) return undefined;
    const group = groups.find((g) => g.id === visibleGroupId);
    if (group && group.room.status === MeetingRoomStatus.Ongoing) {
      outputData = {
        room: activityRoom,
        mode: "one_group",
        group,
        sessions: group.users.flatMap((user) => user.sessions),
        confirmedSessions: [],
        requiresConfirmation: false,
      };
    }
  } else if (audience?.type === MeetingRoomAudienceType.RoomLeader) {
    const leaders: PopulatedUser[] = [];
    groups.forEach((group) => {
      if (group.room.status !== MeetingRoomStatus.Ongoing || (audience.values !== "*" && !audience.values.includes(group.id))) return;
      const leaderId = group.room.leaderUserId;
      if (leaderId) {
        const leader = group.users.find((u) => u.id === leaderId);
        if (leader) {
          leaders.push(leader);
        }
      }
    });
    if (audience.values === "*") {
      outputData = {
        room: activityRoom,
        mode: "all_leaders",
        sessions: leaders.flatMap((user) => user.sessions),
        confirmedSessions: [],
        requiresConfirmation: true,
      };
    } else {
      outputData = {
        room: activityRoom,
        mode: "one_leader",
        group: leaders.length ? leaders[0].group : undefined,
        sessions: leaders.length ? leaders[0].sessions : [], // currently, it's either all or one leader
        confirmedSessions: [],
        requiresConfirmation: true,
      };
    }
  } else if (audience?.type === MeetingRoomAudienceType.HighlightedUser) {
    if (audience.values !== "*") return undefined; // not supported
    const highlightedSessions: PopulatedSession[] = [];
    // Some users are highlighted thro the room
    groups.forEach((group) => {
      if (group.room.status === MeetingRoomStatus.Ongoing && group.room.highlighting === MeetingRoomHighlightingState.ONGOING) {
        highlightedSessions.push(...group.users.flatMap((user) => user.sessions));
      }
    });
    // Some users are highlighted thro the session
    users.forEach((user) => {
      user.sessions.forEach((session) => {
        if (session.session.highlighting === MeetingSessionHighlightingState.ONGOING) {
          highlightedSessions.push(session);
        }
      });
    });
    outputData = {
      room: activityRoom,
      mode: "highlight",
      sessions: highlightedSessions,
      confirmedSessions: [],
      requiresConfirmation: true,
    };
  } else if (audience?.type === MeetingRoomAudienceType.Custom) {
    // TODO-V4 : Temporary HACK
    // This audience does not target any user, it's for the pedagogy expert to talk with experts
    // This will be ultimately replaced by a multi-activity system
    // the peda experts will enlist themselves in it to let the experts know they are here

    // When the peda creates the activity, he enlists himself
    // When the peda is not in the meeting, the activity is not considered,
    // it's important because the experts are not allowed to close the activity, only the peda can,
    // so we could fall in cases where the peda just leaves and the activity closing fails,
    // leaving the experts jailed here

    // when an other peda comes, he adds himself to the activity room,
    // so that we can keep track of peda users ...

    // With The multi-activity system, we won'y have such a problem anymore because everyone
    // will be free to leave/join
    if (
      !activityRoom.users.flatMap((u) => u.sessions).filter((s) => isMeetingSessionRoleGreaterThan(s.session.role, MeetingSessionRole.SUPERSPECTATOR)).length
    )
      return undefined;

    outputData = {
      room: activityRoom,
      mode: "pedagogy",
      sessions: [],
      confirmedSessions: [],
      requiresConfirmation: false,
    };
  }

  if (!outputData) return undefined;

  // 2 - we remove the users who did not enlist yet (confirmed on the navigation popup)

  if (outputData.requiresConfirmation) {
    outputData.confirmedSessions = outputData.sessions.filter((session) => enlistedUserIds?.includes(session.user.id));
  } else {
    outputData.confirmedSessions = outputData.sessions;
  }

  return outputData;
}

export const usePopulatedMeeting__INTERNAL_USE_ONLY = (meetingContainerAPI: MeetingContainerAPI): PopulatedMeeting | undefined => {  
  const [meeting] = useMeetingSelector(meetingContainerAPI, "meeting");
  const [sessions] = useMeetingSelector(meetingContainerAPI, "sessions");
  const [ownSession] = useMeetingSelector(meetingContainerAPI, "session");
  const [receptionSessions] = useMeetingSelector(meetingContainerAPI, "receptionSessions");
  const [rooms] = useMeetingSelector(meetingContainerAPI, "rooms");
  
  const populatedData: PopulatedMeeting | undefined = useMemo(() => {
    // Checking if store data are ready
    if (!meeting) return undefined;
    if (!sessions) return undefined;
    if (!ownSession) return undefined;
    if (!receptionSessions) return undefined;
    if (!rooms) return undefined;
    if (!rooms) return undefined;

    const populatedUsers = _createPopulatedUsers(meeting, sessions, ownSession, receptionSessions);
    const populatedRooms = _createAndInjectPopulatedRooms(rooms, populatedUsers.users);
    const activity = _createActivityData(populatedRooms, populatedUsers.users);

    return { 
      me: populatedUsers.me, 
      users: populatedUsers.users, 
      expertUsers: populatedUsers.expertUsers, 
      studentUsers: populatedUsers.studentUsers, 
      rooms: populatedRooms,
      activity,
    };
  }, [meeting, sessions, ownSession, receptionSessions, rooms]);

  return populatedData;
};
